import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import ModalBody from '../../../modals/modalBody';
import { useTranslate } from '@tolgee/react';
import toast from 'react-hot-toast';
import {
  cancelImageThumbnail,
  clearEditedMediafile,
  setModalUiContent,
  setShowEditImageModal,
  updateBrandEditorCloseState,
  updateEditorCloseState,
  updateMultiImageEditorCloseState,
  updateOverlayCreatorCloseState
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import SchedulingModal from '../Scheduling';
import PlanningConfirmation from '../../cards/planningConfirmation';
import ThumbnailPicker from '../thumbnailPicker';
import ImagesEditor from '../imagesEditor';
import EditorConfirmation from '../../cards/editorExitConfirmation';
import { ALL, PLATFORM_LIST } from '../../../../config/constants';
import MultiImageModifier from '../MultiImageAutoModifier';
import ThumbnailEditorConfirmation from '../../cards/thumbnailEditorConfirmation';
import IGTags from './InstagramTags';
import IGProductTags from './InstagramProductsTags';
import InstagramMusic from './InstagramMusic';
import BrandImagesEditor from '../brandImagesEditor';
import OverlayCreator from '../overlayCreator';
import OverlayEditorConfirmation from '../../cards/overlayEditorConfirmation';
import MediaAltTags from '../mediaAltTags';
import ContentTextBlocks from '../contentTextBlocks';
import { setTextBlockModalUi } from '../../../../redux/slices/textBlocks';
import MediaView from '../mediaView';
import BrowserPluginModal from './browserPluginModal';
import OverlayTemplateImageEditor from '../MediaUploadArea/OverlayTemplateImageEditor';
import {
  getOverlayApplicableMedia,
  getOverlayTemplateEligibleMedia
} from '../../config/overlayTemplateCreator';
import OverlayTemplateSelectorModal from '../MediaUploadArea/OverlayTemplateSelectorModal';
import type { OverlayTemplateDetailDto } from '../../../../api/overlayTemplates';
import { usePlanFeature } from '../../../../utils/subscription/usePlanFeature';

const CreatorMainModals = ({
  selectedEditorPlatform,
  overlayTemplate,
  onOverlayTemplateChange
}: {
  selectedEditorPlatform: string;
  overlayTemplate: OverlayTemplateDetailDto | null;
  onOverlayTemplateChange: (template: OverlayTemplateDetailDto) => void;
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const [showSchedule, setShowSchedule] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [showAltTagsConf, setShowAltTagsConf] = useState(false);
  const [showBrandConf, setShowBrandConf] = useState(false);
  const [showEditorConfirmation, setShowEditorConfirmation] = useState(false);
  const [productTagConf, setProductTagConf] = useState(false);
  const [showThumbnailConf, setShowThumbnailConf] = useState(false);
  const [showOverlayConf, setShowOverlayConf] = useState(false);
  const [showBrowserPluginConf, setShowBrowserPluginConf] = useState(false);
  // Which editor asked for the overlay picker: it decides whose media the
  // overlay lands on, and which modal steps aside for the overlay editor.
  const [templateSelectorSource, setTemplateSelectorSource] = useState<{
    modal: 'multiMediaEditor' | 'mediaEditor';
    /** The one image the choice is for, and the platform it belongs to. */
    uuid?: string;
    platform?: string;
  } | null>(null);
  const [normalModeTemplate, setNormalModeTemplate] =
    useState<OverlayTemplateDetailDto | null>(null);
  const [overlayEditorPlatform, setOverlayEditorPlatform] = useState(ALL);

  const scheduleBtnRef = useRef(null);
  const scheduleModalToggle = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.schedule.toggle
  );
  const recurrence = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.schedulePlanningData.recurrence_as_str
  );

  const savedRecurrence = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.recurrence_as_str
  );

  const time = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.time
  );

  const thumbnailStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.thumbnail
  );
  const overlayStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.brandOverlay
  );
  const IGTagModalState = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.modalStates.IGTags
  );

  const IGProductTagModalState = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.IGProductTags
  );

  const IGMusicModalState = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.IGMusic
  );

  const mediaStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.mediaEditor
  );
  const mediaAltTagsStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.mediaAltTags
  );

  const { mediaViewer } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.modalStates
  );
  const brandMediaModal = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.brandMediaEditor
  );
  const { browserPlugin } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.modalStates
  );
  const EditorChanged = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.allowEditorClose
  );

  const BrandEditorChanged = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.allowBrandEditorClose
  );
  const OverlayEditorChanged = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.allowOverlayCreatorClose
  );

  const multiAutoMediaModal = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.multiMediaEditor
  );
  const templateMediaEditorOpen = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.templateMediaEditor.toggle
  );
  const multiEditorChanged = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.allowMultiImageEditor
  );

  const postData = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postData
  );
  const locationUuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.locationUuid
  );
  const [templateMediaSession, setTemplateMediaSession] = useState<any[]>([]);
  const [templateMediaStartIndex, setTemplateMediaStartIndex] = useState(0);
  const templateMediaEditorWasOpen = useRef(false);
  /**
   * What choosing an overlay asked the editor to open on, held in a ref rather
   * than in state.
   *
   * Opening the editor is a redux dispatch, and that re-renders this component
   * before a setState made in the same handler has landed. The effect below
   * then saw an empty session, took it for "nothing prepared yet", and filled
   * it from the hand-off rule — which is empty once an overlay has been placed.
   * A ref is written immediately, so the effect cannot miss it, and it leaves
   * the effect as the only writer of the session.
   */
  const requestedTemplateMedia = useRef<{
    media: any[];
    startIndex: number;
  } | null>(null);
  useEffect(() => {
    if (!templateMediaEditorOpen) {
      if (templateMediaEditorWasOpen.current) {
        setTemplateMediaSession([]);
        setTemplateMediaStartIndex(0);
        requestedTemplateMedia.current = null;
      }
      templateMediaEditorWasOpen.current = false;
      return;
    }
    templateMediaEditorWasOpen.current = true;
    const requested = requestedTemplateMedia.current;
    if (requested) {
      requestedTemplateMedia.current = null;
      setTemplateMediaSession(requested.media);
      setTemplateMediaStartIndex(requested.startIndex);
      return;
    }
    // Nothing was asked for by hand, so this is the automatic hand-off from
    // the first upload.
    if (templateMediaSession.length > 0) return;
    setTemplateMediaSession(
      getOverlayTemplateEligibleMedia(postData[overlayEditorPlatform]?.media)
    );
  }, [
    postData,
    overlayEditorPlatform,
    templateMediaEditorOpen,
    templateMediaSession.length
  ]);
  const slice = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );
  const platformPostData = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData?.[multiAutoMediaModal.platform]
  );
  // Overlays are a paid plan feature: without it the creator must not offer
  // the template picker at all — the library API behind it refuses the account.
  const overlayTemplatesFeature = usePlanFeature('overlay_templates');
  // Offering the overlay is a choice someone makes in the editor, so it is not
  // tied to the automatic hand-off and does not disappear once that has run.
  const eligibleTemplateMedia = getOverlayApplicableMedia(
    platformPostData?.media
  );
  // Shown whenever there is an image to put an overlay on. The plan decides
  // whether it is locked, not whether it is there at all — an action that
  // vanishes leaves no trace of the feature for anyone who might want it.
  const canUseTemplate = Boolean(eligibleTemplateMedia.length);
  const platformEnabled = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.platformEnabled
  );

  const thumbnailPlatform = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.thumbnail.platform
  );

  const layeredThumbnail = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[thumbnailPlatform]?.layeredImageThumbnail
  );

  const textBlock = useSelector(
    (state: any) => state.textBlocks.textBlockModal
  );

  const checkSchedulingInfo = () => {
    let checkArray = [];

    for (const p of PLATFORM_LIST) {
      if (platformEnabled[p]) {
        if (
          // compare in UTC to avoid timezone issues
          new Date(postData[p].tempPublicationData.datetime).toISOString() ===
            new Date(postData[p].planned_publication_datetime).toISOString() &&
          slice.temporaryPublishingDelayData.totalMinutes ===
            slice.publishingDelayData.totalMinutes &&
          slice.temporaryPublishingDelayData.noPublishDays ===
            slice.publishingDelayData.noPublishDays
        ) {
          checkArray.push(true);
        } else {
          checkArray.push(false);
        }
      }
    }
    console.log('the response', checkArray);
    if (checkArray.includes(false)) {
      return false;
    } else {
      return true;
    }
  };
  const overlayEditorTemplate = normalModeTemplate || overlayTemplate;

  const closeSchedulingModal = () => {
    if (checkSchedulingInfo() && savedRecurrence === recurrence) {
      dispatch(
        setModalUiContent({
          modal: 'schedule',
          attribute: 'toggle',
          value: !scheduleModalToggle
        })
      );
    } else {
      scheduleBtnRef.current.click();
    }
  };
  return (
    <div className="w-100">
      {templateMediaEditorOpen &&
        overlayEditorTemplate &&
        templateMediaSession.length > 0 && (
          <OverlayTemplateImageEditor
            template={overlayEditorTemplate}
            media={templateMediaSession}
          initialIndex={templateMediaStartIndex}
            platform={overlayEditorPlatform}
            onTemplateChange={
              normalModeTemplate ? setNormalModeTemplate : onOverlayTemplateChange
            }
            onComplete={() => {
              dispatch(
                setModalUiContent({
                  modal: 'templateMediaEditor',
                  attribute: 'toggle',
                  value: false
                })
              );
              if (normalModeTemplate) {
                dispatch(
                  clearEditedMediafile({
                    platform: overlayEditorPlatform
                  })
                );
                dispatch(
                  updateMultiImageEditorCloseState({
                    state: false
                  })
                );
                setNormalModeTemplate(null);
                setOverlayEditorPlatform(ALL);
              }
              setTemplateMediaSession([]);
            }}
          />
        )}
      {scheduleModalToggle && (
        <ModalBody
          onClose={() => {
            closeSchedulingModal();
          }}
        >
          <SchedulingModal
            onClose={() => {
              closeSchedulingModal();
            }}
          />
          <button
            type="button"
            className="btn btn-primary d-none"
            onClick={() => setShowSchedule(true)}
            ref={scheduleBtnRef}
          >
            Launch demo modal
          </button>
          <PlanningConfirmation
            handleClose={() => setShowSchedule(false)}
            handleShow={() => setShowSchedule(false)}
            show={showSchedule}
            setModalToggle={() => {
              dispatch(
                setModalUiContent({
                  modal: 'schedule',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}

      {thumbnailStates.toggle && (
        <ModalBody
          onClose={() => {
            setShowThumbnailConf(true);
          }}
        >
          <ThumbnailPicker
            handleClose={() => {
              setShowThumbnailConf(true);
            }}
            handleCloseNoConfirmation={() => {
              dispatch(
                setModalUiContent({
                  modal: 'thumbnail',
                  attribute: 'toggle',
                  value: false
                })
              );
              if (!layeredThumbnail?.media?.original_asset) {
                dispatch(cancelImageThumbnail({ platform: thumbnailPlatform }));
              }
            }}
            platform={thumbnailStates.platform}
          />

          <ThumbnailEditorConfirmation
            handleClose={() => {
              setShowThumbnailConf(false);
            }}
            handleClear={() => {
              setShowThumbnailConf(false);
              dispatch(
                setModalUiContent({
                  modal: 'thumbnail',
                  attribute: 'toggle',
                  value: false
                })
              );
              if (!layeredThumbnail?.media?.original_asset) {
                dispatch(cancelImageThumbnail({ platform: thumbnailPlatform }));
              }
            }}
            show={showThumbnailConf}
          />
        </ModalBody>
      )}

      {overlayStates.toggle && (
        <ModalBody
          onClose={
            !OverlayEditorChanged
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'brandOverlay',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowOverlayConf(true)
          }
        >
          <OverlayCreator
            handleClose={
              !OverlayEditorChanged
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'brandOverlay',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                  }
                : () => setShowOverlayConf(true)
            }
            handleCloseNoConfirmation={() => {
              dispatch(
                setModalUiContent({
                  modal: 'brandOverlay',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />

          <OverlayEditorConfirmation
            handleClose={() => {
              setShowOverlayConf(false);
            }}
            handleClear={() => {
              setShowOverlayConf(false);
              dispatch(
                updateOverlayCreatorCloseState({
                  state: false
                })
              );
              dispatch(
                setModalUiContent({
                  modal: 'brandOverlay',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
            show={showOverlayConf}
          />
        </ModalBody>
      )}

      {browserPlugin.toggle && (
        <ModalBody
          onClose={
            browserPlugin?.allowClose
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'browserPlugin',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowBrowserPluginConf(true)
          }
        >
          <BrowserPluginModal
            handleClose={
              browserPlugin?.allowClose
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'browserPlugin',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                  }
                : () => setShowBrowserPluginConf(true)
            }
            handleCloseNoConf={() => {
              dispatch(
                setModalUiContent({
                  modal: 'browserPlugin',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />

          <EditorConfirmation
            handleClose={() => {
              setShowBrowserPluginConf(false);
            }}
            handleClear={() => {
              setShowBrowserPluginConf(false);
              dispatch(
                setModalUiContent({
                  modal: 'browserPlugin',
                  attribute: 'allowClose',
                  value: true
                })
              );
              dispatch(
                setModalUiContent({
                  modal: 'browserPlugin',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
            show={showBrowserPluginConf}
          />
        </ModalBody>
      )}

      {mediaStates.toggle && ( //TODO add the button above to buttonsBar component and add global state for edit image modal in ui
        <ModalBody
          onClose={
            !EditorChanged
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'mediaEditor',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowMedia(true)
          }
        >
          <ImagesEditor
            currentPlatform={mediaStates?.platform}
            currentIndex={mediaStates?.currentIndex}
            onUseTemplate={(target) =>
              setTemplateSelectorSource({ modal: 'mediaEditor', ...target })
            }
            canUseTemplate={
              getOverlayApplicableMedia(postData[mediaStates?.platform]?.media)
                .length > 0
            }
            overlaysAvailable={overlayTemplatesFeature.isAvailable}
            handleClose={
              !EditorChanged
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'mediaEditor',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                  }
                : () => setShowMedia(true)
            }
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => {
          setShowMedia(false);

          dispatch(
            updateEditorCloseState({
              state: false
            })
          );
        }}
        handleClear={() => {
          setShowMedia(false);

          dispatch(
            setModalUiContent({
              modal: 'mediaEditor',
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'mediaEditor',
              attribute: 'currentIndex',
              value: null
            })
          );
          dispatch(
            updateEditorCloseState({
              state: false
            })
          );
        }}
        show={showMedia}
      />

      {mediaViewer.toggle && ( //TODO add the button above to buttonsBar component and add global state for edit image modal in ui
        <ModalBody
          onClose={() => {
            dispatch(
              setModalUiContent({
                modal: 'mediaViewer',
                attribute: 'toggle',
                value: false
              })
            );
          }}
        >
          <MediaView
            currentPlatform={mediaViewer?.platform}
            currentIndex={mediaViewer?.currentIndex}
            handleClose={() => {
              dispatch(
                setModalUiContent({
                  modal: 'mediaViewer',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}

      {brandMediaModal.toggle && ( //TODO add the button above to buttonsBar component and add global state for edit image modal in ui
        <ModalBody
          onClose={
            !BrandEditorChanged
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'brandMediaEditor',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                  dispatch(
                    setModalUiContent({
                      modal: 'brandMediaEditor',
                      attribute: 'cropMode',
                      value: false
                    })
                  );
                }
              : () => setShowBrandConf(true)
          }
        >
          <BrandImagesEditor
            currentPlatform={brandMediaModal?.platform}
            currentIndex={brandMediaModal?.currentIndex}
            cropMode={brandMediaModal?.cropMode}
            handleClose={
              !BrandEditorChanged
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'brandMediaEditor',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                    dispatch(
                      setModalUiContent({
                        modal: 'brandMediaEditor',
                        attribute: 'cropMode',
                        value: false
                      })
                    );
                  }
                : () => setShowBrandConf(true)
            }
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => {
          setShowBrandConf(false);

          dispatch(
            updateBrandEditorCloseState({
              state: false
            })
          );
        }}
        handleClear={() => {
          setShowBrandConf(false);
          dispatch(
            setModalUiContent({
              modal: 'brandMediaEditor',
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'brandMediaEditor',
              attribute: 'currentIndex',
              value: null
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'brandMediaEditor',
              attribute: 'cropMode',
              value: false
            })
          );
          dispatch(
            updateBrandEditorCloseState({
              state: false
            })
          );
        }}
        show={showBrandConf}
      />

      {mediaAltTagsStates?.toggle && ( //TODO add the button above to buttonsBar component and add global state for edit image modal in ui
        <ModalBody
          onClose={
            !EditorChanged
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'mediaAltTags',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowAltTagsConf(true)
          }
        >
          <MediaAltTags
            currentPlatform={mediaAltTagsStates?.platform}
            currentIndex={mediaAltTagsStates?.currentIndex}
            handleClose={
              mediaAltTagsStates?.allowClose
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'mediaAltTags',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                  }
                : () => setShowAltTagsConf(true)
            }
            handleCloseNoConf={() => {
              dispatch(
                setModalUiContent({
                  modal: 'mediaAltTags',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => {
          setShowAltTagsConf(false);
        }}
        handleClear={() => {
          setShowAltTagsConf(false);
          dispatch(
            setModalUiContent({
              modal: 'mediaAltTags',
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'mediaAltTags',
              attribute: 'currentIndex',
              value: null
            })
          );

          dispatch(
            setModalUiContent({
              modal: 'mediaAltTags',
              attribute: 'allowClose',
              value: true
            })
          );
        }}
        show={showAltTagsConf}
      />

      {multiAutoMediaModal.toggle && (
        <ModalBody
          onClose={
            !multiEditorChanged
              ? () => {
                  dispatch(
                    clearEditedMediafile({
                      platform: ALL
                    })
                  );
                  dispatch(
                    setModalUiContent({
                      modal: 'multiMediaEditor',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowEditorConfirmation(true)
          }
        >
          <MultiImageModifier
            handleClose={
              !multiEditorChanged
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'multiMediaEditor',
                        attribute: 'toggle',
                        value: false
                      })
                    );

                    dispatch(
                      clearEditedMediafile({
                        platform: ALL
                      })
                    );
                  }
                : () => setShowEditorConfirmation(true)
            }
            platform={multiAutoMediaModal.platform}
            handleCloseNoConfirmation={() => {
              dispatch(
                setModalUiContent({
                  modal: 'multiMediaEditor',
                  attribute: 'toggle',
                  value: false
                })
              );
              dispatch(
                clearEditedMediafile({
                  platform: ALL
                })
              );
            }}
            newUploadedAsset={platformPostData?.unEditedMedia}
            count={
              platformPostData.media?.length -
              platformPostData.unEditedMedia?.length
            }
            onUseTemplate={(target) =>
              setTemplateSelectorSource({
                modal: 'multiMediaEditor',
                ...target
              })
            }
            canUseTemplate={canUseTemplate}
            overlaysAvailable={overlayTemplatesFeature.isAvailable}
          />
        </ModalBody>
      )}
      <OverlayTemplateSelectorModal
        show={Boolean(templateSelectorSource)}
        locationUuid={locationUuid}
        onClose={() => setTemplateSelectorSource(null)}
        onSelect={(template) => {
          // Both editors offer this, and they are separate modals over
          // different platforms — so the overlay is applied to the media of
          // whichever one asked, and only that one is closed behind it.
          const platform =
            templateSelectorSource.platform ??
            (templateSelectorSource.modal === 'mediaEditor'
              ? mediaStates?.platform
              : multiAutoMediaModal.platform);
          // The overlay is for the one image being edited, not for the post:
          // whoever asked was looking at a single image. The editor still
          // gets every image, because it is the one with the list on the
          // left — it just opens on the one that was being edited. Found by
          // uuid: both editors page through their own filtered lists, so a
          // position in them means nothing here.
          const media = getOverlayApplicableMedia(postData[platform]?.media);
          const pickedIndex = media.findIndex(
            (item) => item.uuid === templateSelectorSource.uuid
          );
          if (!media.length) {
            // The picker closes itself right after this, so saying nothing
            // would leave the click looking like it simply shut the editor.
            toast.error(
              t(
                'lomavisCreator.overlayPicker.mediaUnavailable',
                'That image is no longer available. Please pick the image again.'
              )
            );
            return;
          }

          const closing = templateSelectorSource.modal;
          requestedTemplateMedia.current = {
            media,
            startIndex: pickedIndex > 0 ? pickedIndex : 0
          };
          setTemplateSelectorSource(null);
          setNormalModeTemplate(template);
          setOverlayEditorPlatform(platform);
          dispatch(
            setModalUiContent({
              modal: closing,
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'templateMediaEditor',
              attribute: 'toggle',
              value: true
            })
          );
        }}
      />
      <EditorConfirmation
        handleClose={() => setShowEditorConfirmation(false)}
        handleClear={() => {
          dispatch(
            setModalUiContent({
              modal: 'multiMediaEditor',
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            updateMultiImageEditorCloseState({
              state: false
            })
          );
          dispatch(
            clearEditedMediafile({
              platform: ALL
            })
          );
        }}
        show={showEditorConfirmation}
      />

      {IGTagModalState.toggle && (
        <ModalBody
          onClose={() => {
            dispatch(
              setModalUiContent({
                modal: 'IGTags',
                attribute: 'toggle',
                value: false
              })
            );
          }}
        >
          <IGTags
            handleClose={() => {
              dispatch(
                setModalUiContent({
                  modal: 'IGTags',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}

      {/* IGProductTags Modal */}
      {IGProductTagModalState.toggle && (
        <ModalBody
          onClose={
            IGProductTagModalState.allowClose
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'IGProductTags',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                  dispatch(
                    setModalUiContent({
                      modal: 'IGProductTags',
                      attribute: 'allowClose',
                      value: true
                    })
                  );
                }
              : () => setProductTagConf(true)
          }
        >
          <IGProductTags
            handleClose={
              IGProductTagModalState.allowClose
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'IGProductTags',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                    dispatch(
                      setModalUiContent({
                        modal: 'IGProductTags',
                        attribute: 'allowClose',
                        value: true
                      })
                    );
                  }
                : () => setProductTagConf(true)
            }
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => setProductTagConf(false)}
        handleClear={() => {
          setProductTagConf(false);

          dispatch(
            setModalUiContent({
              modal: 'IGProductTags',
              attribute: 'toggle',
              value: false
            })
          );

          dispatch(
            setModalUiContent({
              modal: 'IGProductTags',
              attribute: 'allowClose',
              value: true
            })
          );
        }}
        show={productTagConf}
      />

      {/* InstagramMusic Modal */}
      {IGMusicModalState?.toggle && (
        <ModalBody
          onClose={() => {
            dispatch(
              setModalUiContent({
                modal: 'IGMusic',
                attribute: 'toggle',
                value: false
              })
            );
          }}
        >
          <InstagramMusic
            handleClose={() => {
              dispatch(
                setModalUiContent({
                  modal: 'IGMusic',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}

      {/* ContentTextBlocks Modal */}
      {textBlock.toggle && (
        <ModalBody
          onClose={() => {
            dispatch(
              setTextBlockModalUi({
                attribute: 'toggle',
                value: false
              })
            );
          }}
        >
          <ContentTextBlocks
            currentPlatform={textBlock?.platform}
            handleClose={() => {
              dispatch(
                setTextBlockModalUi({
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}
    </div>
  );
};

export default CreatorMainModals;
